import { useKeyPressAction } from "@/hooks/useKeyPress";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import React from "react";

interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  children: React.ReactNode;
}

/**
 * Right side drawer that opens as:
 **   an overlay on screens smaller than 1800px.
 **   a block on the right side on screens 1800px and larger.

 * @readonly Parent component must have the classNames 'flex flex-row justify-between' for the drawer layout to work properly.
 * @param {boolean} isOpen - The current open/close state of the drawer from the parent component.
 * @param setIsOpen - Sets the open/close state of the drawer in the parent component.
 * @param children - The JSX or component(s) will be the content of the drawer.
 * @example
 * return (
 *    <div className="flex flex-row justify-between">
 *        <div>Regular page</div>
 *        <Drawer isOpen={showDrawer} setIsOpen={(open) => setShowDrawer(open)}>{children}</Drawer>
 *    </div>
 * )
 */

const Drawer = ({ isOpen, setIsOpen, children }: Props) => {
  useKeyPressAction({ targetKey: "Escape", fn: () => setIsOpen(false) });

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="drawer-rightside"
          initial={{ x: "2rem", opacity: 0 }}
          animate={{ x: "0rem", opacity: 1 }}
          exit={{ x: "2rem", opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{ height: "100vh", zIndex: 50 }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="absolute right-0 top-0 bottom-0 w-1/2 xxxl:sticky xxxl:w-1/2 xxxl:z-0"
        >
          <div className="absolute xxxl:w-full right-0 bottom-0 top-0 inset-y-0 p-6 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.1)] xxxl:shadow-none overflow-auto bg-white dark:bg-brand-gray-900 border-l border-b border-gray-200 dark:border-gray-950">
            <span
              onClick={() => setIsOpen(false)}
              className="p-4 cursor-pointer absolute top-0 right-0"
              role="button"
              tabIndex={-1}
              aria-label="Close app files"
            >
              <X
                size={16}
                fontWeight="bold"
                className="text-brand-gray-600 dark:text-brand-gray-200"
              />
            </span>
            <div>{children}</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Drawer;
